
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cognitionService } from '../../cognition/cognitionService';
import type { CognitiveObject } from '../../cognition/cognitionService';
import { VotingPanel } from '../../governance/ui/VotingPanel';

export const VotingObjectPage: React.FC = () => {
    const { objectId } = useParams<{ objectId: string }>();
    const [object, setObject] = useState<CognitiveObject | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const load = async () => {
        if (!objectId) return;
        try {
            const data = await cognitionService.getObject(objectId);
            setObject(data);
        } catch (e) {
            console.error(e);
            setError("Failed to load object. Ensure backend has GET /objects/:id endpoint.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [objectId, refreshTrigger]);

    const handleUpdate = () => {
        setRefreshTrigger(p => p + 1);
    };

    if (loading) return <div className="p-8 dark:text-white">Loading object context...</div>;
    if (error) return <div className="p-8 text-red-600 dark:text-red-400">{error}</div>;
    if (!object) return <div className="p-8 dark:text-white">Object not found.</div>;

    return (
        <div className="flex flex-col h-screen bg-gray-50 dark:bg-slate-900">
            <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 px-6 py-4 flex items-center gap-4">
                <Link to="/voting" className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                    &larr; Back to Index
                </Link>
                <div>
                    <h1 className="font-bold text-lg dark:text-white">{object.content?.question || 'Untitled Object'}</h1>
                    <div className="flex gap-2 text-xs items-center mt-1">
                        <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded-full font-medium">
                            {object.current_state}
                        </span>
                        <span className="text-gray-400 font-mono">{object.id}</span>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-8">
                <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-lg border dark:border-slate-700 overflow-hidden">
                    <div className="p-6 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50">
                        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            Isolated Voting Panel
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Validating component isolation. No canvas dependencies.
                        </p>
                    </div>

                    <div className="p-6">
                        <VotingPanel
                            objectId={object.id}
                            objectState={object.current_state}
                            onUpdate={handleUpdate}
                            isActiveTab={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
